/**
 * **Renewal on use.** `ADR 051` §2.
 *
 * Before a credentialled request goes out, the access token's own `exp` is read. If it has passed
 * — or is inside the skew window — the session is renewed *first*, rather than sending a request
 * that is expected to fail. A 401 that arrives anyway (the clock was wrong, the token was
 * revoked) gets **one** renewal and **one** retry; a second 401 ends the session.
 *
 * ## Why this is a module-level registry rather than a value passed down
 *
 * Both transports need it and neither is inside React. Each app's `src/lib/api-client.ts` builds
 * its `createApiRequest` **at module load**, long before `<AuthProvider>` mounts, so there is
 * nothing to hand it. This is the same shape as `hooks/session-recheck.ts` and for the same reason.
 *
 * It is also what makes single-flight real. The renewal has to be one renewal across *both*
 * clients — the `/auth/*` client and the domain transport — and across every query that expires
 * at the same moment. A per-client latch would still let two go out.
 *
 * ## Why single-flight
 *
 * **Refresh tokens rotate** (Sumit's ruling of 2026-09-03, `auth-stack.ts`): every renewal
 * returns a new refresh token, and the one it replaced stops working thirty seconds later. So two
 * renewals racing in one tab are not just wasteful; once the grace period has passed, the loser
 * holds a token the pool refuses. This latch is what stands between a page load and that refusal,
 * so it must not be removed as an optimisation.
 *
 * It also stops the **thundering herd**: a console mounts eight queries, they all find the same
 * expired token in the same tick, and without a latch that is eight renewals for one session —
 * eight round trips the user waits on, eight chances for one to fail, and eight `setToken` writes
 * racing each other into `localStorage`.
 *
 * The latch is per tab. Tabs share the stored refresh token, so a renewal refused because another
 * tab exchanged it first is handled in {@link createSessionRenewer}, not here.
 */
import { AuthApiError } from '../errors.js';
/**
 * How close to expiry counts as expired.
 *
 * A minute, against a 30-minute admin access token and a 60-minute member one. It covers ordinary
 * client-clock drift and the flight time of the request itself: a token with 200ms left passes
 * `exp` while it is on the wire, and the point of renewing before sending is to not make that
 * round trip twice.
 */
export const RENEWAL_SKEW_MS = 60_000;
let renewer = null;
let inFlight = null;
let generation = 0;
/**
 * Installs the renewer. Called by `<AuthProvider>`, which owns the credentials.
 *
 * **Registered during render, not from an effect**, and that is load-bearing: React runs a
 * child's effects *before* its parent's, so a `/auth/me` fired from `useAuthMe`'s mount effect
 * would otherwise go out against an expired token with no renewer installed — which is precisely
 * the page load this feature exists for, a member returning the next morning. `session-recheck.ts`
 * hit the same ordering and solved it by latching the request; latching is wrong here, because a
 * page with no `<AuthProvider>` would then block every request forever waiting for a provider
 * that is never coming.
 */
export function registerSessionRenewer(next) {
    renewer = next;
}
/** Removes a renewer, if it is still the installed one. Called on unmount. */
export function clearSessionRenewer(previous) {
    if (renewer !== previous)
        return;
    renewer = null;
    inFlight = null;
}
/**
 * Runs at most one renewal at a time; concurrent callers await the same one.
 *
 * `Promise.resolve().then(renew)` rather than calling the renewer directly: it guarantees the
 * first continuation runs in a microtask, *after* `inFlight` has been assigned, so a renewer that
 * throws synchronously cannot clear a latch that was not yet set and strand it non-null forever.
 */
function renewOnce() {
    if (inFlight !== null)
        return inFlight;
    const renew = renewer;
    if (renew === null)
        return Promise.resolve(null);
    const mine = (generation += 1);
    const run = Promise.resolve()
        .then(renew)
        .catch(() => null)
        .then((token) => {
        // Only the latest renewal clears the latch. A renewer replaced mid-flight (a remounted
        // provider) must not have its successor's in-flight renewal cancelled by its predecessor.
        if (generation === mine)
            inFlight = null;
        return token;
    });
    inFlight = run;
    return run;
}
/**
 * Decodes an access token's `exp`, as milliseconds since the epoch.
 *
 * Returns null when the token is not a JWT or carries no numeric `exp`. **That is not an error
 * and must not be read as "expired"** — a Kratos session token is opaque and has no readable
 * expiry, and treating "I cannot tell" as "renew" would renew before every single request on that
 * provider.
 */
export function accessTokenExpiresAt(token) {
    const segments = token.split('.');
    if (segments.length < 2)
        return null;
    const payload = segments[1];
    if (payload === undefined)
        return null;
    try {
        const normalised = payload.replaceAll('-', '+').replaceAll('_', '/');
        const padded = normalised.padEnd(normalised.length + ((4 - (normalised.length % 4)) % 4), '=');
        const fromBase64 = globalThis.atob;
        if (typeof fromBase64 !== 'function')
            return null;
        // `atob` yields a *binary* string, one char per byte. Decoding it as UTF-8 rather than
        // handing it straight to `JSON.parse` matters: an `email` or `name` claim with any character
        // above U+007F would otherwise make the payload invalid JSON, this function would answer null,
        // and that account's session would silently never renew — a defect visible only to the people
        // whose names have accents in them.
        const bytes = Uint8Array.from(fromBase64(padded), (character) => character.charCodeAt(0));
        const claims = JSON.parse(new TextDecoder().decode(bytes));
        if (typeof claims.exp !== 'number' || !Number.isFinite(claims.exp))
            return null;
        return claims.exp * 1000;
    }
    catch {
        return null;
    }
}
/** Whether this token is close enough to expiry that it should be renewed before it is sent. */
export function isDueForRenewal(token, now = Date.now()) {
    const expiresAt = accessTokenExpiresAt(token);
    if (expiresAt === null)
        return false;
    return expiresAt - now <= RENEWAL_SKEW_MS;
}
/**
 * The renewal a transport calls into.
 *
 * A module-level object rather than a factory, because the state it closes over is module-level
 * and two of them would be two latches.
 */
export const sessionRenewal = {
    /**
     * The token to actually send, renewing first when the one in hand is spent.
     *
     * A **null** current token returns null without renewing. A browser with no access token is a
     * signed-out browser: expiry does not clear the stored token, so a member returning after a
     * fortnight still has a stale one to read an `exp` off. Renewing on null would mean every
     * anonymous page view on the public site asking whether it has a session.
     *
     * A **failed** renewal returns the token unchanged rather than null, and does not decide
     * anything: the request goes out, the server answers, and if that answer is a 401 the retry
     * path below reaches the one place a session ends. Substituting null here would be this
     * function deciding the session was over, which is `ADR 051` §3's whole objection.
     */
    async credentialFitToSend(current) {
        if (current === null || current.length === 0)
            return current;
        if (!isDueForRenewal(current))
            return current;
        const renewed = await renewOnce();
        return renewed ?? current;
    },
    /**
     * One renewal after a refusal, for the 401 that arrives anyway.
     *
     * Joins an in-flight renewal rather than starting a second: when eight queries are refused in
     * the same tick, the first one's renewal is the answer to all eight.
     */
    async renewAfterRefusal() {
        return renewOnce();
    },
};
/**
 * Builds the renewer `<AuthProvider>` installs: exchange the stored refresh credential for a new
 * access token, and decide — once, here — whether a failure means the session is over.
 *
 * A function rather than four lines inside the provider so that a test can drive the real one.
 * A test that re-implements this would be asserting against its own copy, which is register
 * entry 11 and is worth less than no test at all.
 */
export function createSessionRenewer(dependencies) {
    const { client, setToken, refreshCredentials } = dependencies;
    return async () => {
        const sent = refreshCredentials.read();
        try {
            const result = await client.refreshSession();
            return result.sessionToken;
        }
        catch (error) {
            // **Another tab may have renewed first** (TW 44272642). Tabs share the stored refresh
            // token, and the pool rotates it on every renewal, so this tab's copy can be refused because
            // another tab has already exchanged it. If the store now holds a different token, that tab
            // renewed in the meantime: renew once more with that one, rather than signing this tab out
            // and clearing the other tab's fresh credential with it. Only once; a second refusal ends
            // the session below.
            let failure = error;
            const stored = refreshCredentials.read();
            if (failure instanceof AuthApiError && stored !== null && stored !== sent) {
                try {
                    const result = await client.refreshSession();
                    return result.sessionToken;
                }
                catch (retryError) {
                    failure = retryError;
                }
            }
            // **The one place a session ends, and the distinction that decides it.** A server that
            // *answered* and refused — an expired or revoked refresh token, which is what thirty days
            // of not opening the app looks like — is a session over: both credentials go, the provider's
            // effect clears the user, and the app's redirector carries `next`. `ADR 051` §3.
            //
            // A network failure is not an answer. `fetch` rejects with a `TypeError` when the tab is
            // offline or the API is briefly unreachable, and signing somebody out over a dropped
            // connection would make a train tunnel a logout. The request that prompted this renewal
            // fails on its own terms and the next one tries again.
            if (failure instanceof AuthApiError) {
                setToken(null);
                refreshCredentials.clear();
            }
            return null;
        }
    };
}
/**
 * Drops all module state. **Tests only.**
 *
 * The registry is module-level, so a renewer left installed by one test is a renewer the next
 * test's request finds — which is how `session-recheck-race.test.ts`'s second test first passed
 * while proving nothing.
 */
export function resetSessionRenewalForTests() {
    renewer = null;
    inFlight = null;
    generation = 0;
}
//# sourceMappingURL=session-renewal.js.map